import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useAuth } from "./useAuth";
import { authService } from "@/services/AuthService";
import { resolveEatzyMediaUrl } from "@/lib/utils";
import { ensureFreshEatzyToken } from "@/lib/eatzyTokenGuard";
import { logger } from "@/lib/logger";

export interface SavedAddress {
  id: number;
  label?: string;
  streetAddress: string;
  apartment?: string;
  city: string;
  state?: string;
  zipCode: string;
  country: string;
  latitude?: number;
  longitude?: number;
  isPrimary: boolean;
  deliveryInstructions?: string;
  placeId?: string;
  doorCode?: string;
  businessName?: string;
}

export interface SavedPhone {
  id: number;
  phoneNumber: string;
  countryCode: string;
  isPrimary: boolean;
  isVerified: boolean;
}

export interface UserProfile {
  userId: number | string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  displayName?: string; // First Name + Last Initial (e.g., "Sarah C.")
  profilePicture?: string;
  phone?: string;
  dateOfBirth?: string;
  bio?: string;
  emailVerified: boolean;
  trustScore?: number;
  trustTier?: "newbie" | "reliable" | "trusted" | "elite";
}

interface UserProfileContextType {
  profile: UserProfile | null;
  addresses: SavedAddress[];
  phones: SavedPhone[];
  primaryAddress: SavedAddress | null;
  kudozBalance: number;
  kudozSynced: boolean;
  kudozLinked: boolean;
  unreadAlertCount: number;
  isLoading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
  refreshAddresses: () => Promise<void>;
  refreshPhones: () => Promise<void>;
  refreshKudozBalance: () => Promise<void>;
  updateKudozBalance: (newBalance: number) => void;
  addAddress: (
    address: Omit<SavedAddress, "id">
  ) => Promise<SavedAddress | null>;
  updateAddress: (
    id: number,
    address: Partial<SavedAddress>
  ) => Promise<boolean>;
  deleteAddress: (id: number) => Promise<boolean>;
  setPrimaryAddress: (id: number) => Promise<boolean>;
  cleanupAddresses: () => Promise<{ deletedCount: number; keptCount: number }>;
}

const UserProfileContext = createContext<UserProfileContextType | null>(null);

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const { token, isAuthenticated, user: authUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [addresses, setAddresses] = useState<SavedAddress[]>([]);
  const [phones, setPhones] = useState<SavedPhone[]>([]);
  const [kudozBalance, setKudozBalance] = useState<number>(0);
  const [kudozSynced, setKudozSynced] = useState<boolean>(false);
  const [kudozLinked, setKudozLinked] = useState<boolean>(true);
  const [unreadAlertCount, setUnreadAlertCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const primaryAddress =
    addresses.find((a) => a.isPrimary) || addresses[0] || null;

  const prevUserIdRef = useRef<string | number | null>(null);
  const currentAuthUserId = authUser?.id || authUser?.email || null;

  useEffect(() => {
    if (
      prevUserIdRef.current !== null &&
      currentAuthUserId !== null &&
      prevUserIdRef.current !== currentAuthUserId
    ) {
      logger.log(
        "[UserProfileProvider] User changed from",
        prevUserIdRef.current,
        "to",
        currentAuthUserId,
        "- clearing stale data"
      );
      setAddresses([]);
      setPhones([]);
      setProfile(null);
      setKudozBalance(0);
      setKudozSynced(false);
      setKudozLinked(true);
    }
    prevUserIdRef.current = currentAuthUserId;
  }, [currentAuthUserId]);

  const fetchProfile = useCallback(async () => {
    if (!token) return;

    try {
      // Fetch both Eatzy profile and local user data in parallel
      const [eatzyProfileRaw, localUserResponse] = await Promise.all([
        authService.getEatzyProfile(),
        fetch("/api/user", {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null),
      ]);

      if (!eatzyProfileRaw) return;

      const eatzyProfile = eatzyProfileRaw as Record<string, unknown>;

      // Get avatar from local user if available (local takes priority)
      const localAvatar = localUserResponse?.avatar;
      const localDisplayName = localUserResponse?.displayName;
      const localBio = localUserResponse?.bio;

      // Set unread alert count from local user response
      if (localUserResponse?.unreadAlertCount !== undefined) {
        setUnreadAlertCount(localUserResponse.unreadAlertCount);
      }

      const mappedProfile: UserProfile = {
        userId: (eatzyProfile.user_id ?? eatzyProfile.id) as string | number,
        email: eatzyProfile.email as string,
        name:
          localDisplayName ||
          (eatzyProfile.username as string | undefined) ||
          (eatzyProfile.name as string | undefined) ||
          (eatzyProfile.displayName as string | undefined) ||
          `${eatzyProfile.firstName || eatzyProfile.firstname || eatzyProfile.first_name || ""} ${eatzyProfile.lastName || eatzyProfile.lastname || eatzyProfile.last_name || ""}`.trim(),
        firstName: (eatzyProfile.firstName ||
          eatzyProfile.firstname ||
          eatzyProfile.first_name) as string | undefined,
        lastName: (eatzyProfile.lastName ||
          eatzyProfile.lastname ||
          eatzyProfile.last_name) as string | undefined,
        displayName: localDisplayName || undefined, // Preserve locally stored displayName
        profilePicture: resolveEatzyMediaUrl(
          localAvatar || eatzyProfile.profile_picture || eatzyProfile.avatar
        ),
        phone: eatzyProfile.phone as string | undefined,
        dateOfBirth: (eatzyProfile.date_of_birth ||
          eatzyProfile.dateOfBirth) as string | undefined,
        emailVerified: (eatzyProfile.email_verified ??
          eatzyProfile.emailVerified ??
          true) as boolean,
        bio: (localBio || eatzyProfile.bio) as string | undefined,
      };

      setProfile(mappedProfile);
    } catch (err) {
      logger.error("[useUserProfile] Failed to fetch profile:", err);
    }
  }, [token]);

  const getAddressesCacheKey = useCallback(() => {
    if (!authUser) return null;
    const userId = authUser.id || authUser.email;
    return `user_addresses_cache_${userId}`;
  }, [authUser]);

  const fetchAddresses = useCallback(async () => {
    if (!token) return;

    const cacheKey = getAddressesCacheKey();

    try {
      const response = await authService.eatzyApiCall("/account/address/all");

      if (response.ok) {
        const data = await response.json();
        const addressList = Array.isArray(data) ? data : data.addresses || [];

        if (Array.isArray(addressList) && addressList.length > 0) {
          const mappedAddresses: SavedAddress[] = addressList.map((a) => ({
            id: a.id || a.address_id,
            label: a.label || undefined,
            streetAddress: a.address || a.street_address,
            apartment: a.addition?.suite || a.apartment,
            city: a.city,
            state: a.state,
            zipCode: a.zip || a.zip_code,
            country: a.country || "US",
            latitude: a.latitude,
            longitude: a.longitude,
            isPrimary: a.primary ?? a.is_primary ?? false,
            deliveryInstructions: a.addition?.notes || a.delivery_instructions,
          }));
          logger.log(
            "[useUserProfile] Mapped addresses:",
            mappedAddresses.length,
            "items"
          );
          setAddresses(mappedAddresses);
          if (cacheKey) {
            localStorage.setItem(cacheKey, JSON.stringify(mappedAddresses));
          }
        } else {
          logger.log("[useUserProfile] No addresses returned from API");
          setAddresses([]);
          if (cacheKey) {
            localStorage.removeItem(cacheKey);
          }
        }
      } else {
        logger.warn(
          "[useUserProfile] Address API returned status:",
          response.status
        );
        if (cacheKey) {
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            logger.log("[useUserProfile] Using cached addresses as fallback");
            setAddresses(JSON.parse(cached));
          }
        }
      }
    } catch (err) {
      logger.error("[useUserProfile] Failed to fetch addresses:", err);
      if (cacheKey) {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          logger.log("[useUserProfile] Using cached addresses as fallback");
          setAddresses(JSON.parse(cached));
        }
      }
    }
  }, [token, getAddressesCacheKey]);

  const fetchPhones = useCallback(async () => {
    if (!token) return;

    try {
      // Use /api/user/phones (not the generic proxy /api/eatzy/account/phone/all).
      // The proxy route passes x-user-eatzy-token which may be stale/expired and
      // causes a 403. The /api/user/phones handler uses the Kudoz JWT (Authorization
      // header) directly via EatzyApiClient, which is the same token that works for
      // cart-set and checkout-start.
      const response = await fetch("/api/user/phones", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        // /api/user/phones returns { success, phones: [{ id, number, primary, active }] }
        const phoneList = Array.isArray(data) ? data : data.phones || [];

        if (Array.isArray(phoneList) && phoneList.length > 0) {
          const mappedPhones: SavedPhone[] = phoneList.map((p) => ({
            id: p.id || p.phone_id,
            phoneNumber: p.number || p.phone_number,
            countryCode: p.country_code || p.countryCode || "+1",
            isPrimary: p.primary ?? p.is_primary ?? p.isPrimary ?? false,
            isVerified: p.is_verified ?? p.active ?? p.isVerified ?? true,
          }));
          logger.log(
            "[useUserProfile] Mapped phones:",
            mappedPhones.length,
            "items"
          );
          setPhones(mappedPhones);
        } else {
          logger.log("[useUserProfile] No phones returned from API");
        }
      }
    } catch (err) {
      logger.error("[useUserProfile] Failed to fetch phones:", err);
    }
  }, [token]);

  // Sync Kudoz wallet with external Kudoz API
  const syncKudozWallet = useCallback(async () => {
    if (!token) return;

    try {
      logger.log("[useUserProfile] Syncing Kudoz wallet...");
      const walletHeaders: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
      const eatzyUserToken = await ensureFreshEatzyToken();
      if (eatzyUserToken) walletHeaders["X-User-Eatzy-Token"] = eatzyUserToken;

      const response = await fetch("/api/kudoz/sync-wallet", {
        method: "POST",
        headers: walletHeaders,
      });

      if (response.ok) {
        const data = await response.json();
        logger.log("[useUserProfile] Kudoz wallet sync response:", data);
        if (data.linked === false) {
          setKudozLinked(false);
        } else if (data.success) {
          setKudozSynced(true);
          setKudozLinked(true);
        }
      } else {
        logger.warn(
          "[useUserProfile] Kudoz wallet sync failed:",
          response.status
        );
      }
    } catch (err) {
      logger.error("[useUserProfile] Failed to sync Kudoz wallet:", err);
    }
  }, [token]);

  // Fetch Kudoz balance from external Kudoz API
  const fetchKudozBalance = useCallback(async () => {
    if (!token) return;

    try {
      logger.log(
        "[useUserProfile] Fetching Kudoz balance from external API..."
      );
      const balanceHeaders: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };
      const eatzyUserToken = await ensureFreshEatzyToken();
      if (eatzyUserToken) balanceHeaders["X-User-Eatzy-Token"] = eatzyUserToken;

      const response = await fetch("/api/kudoz/external-balance", {
        headers: balanceHeaders,
      });

      if (response.ok) {
        const data = await response.json();
        logger.log("[useUserProfile] External balance response:", data);
        if (data.linked === false) {
          setKudozLinked(false);
          setKudozBalance(0);
        } else if (data.success && typeof data.balance === "number") {
          setKudozBalance(data.balance);
          setKudozSynced(true);
          setKudozLinked(true);
          logger.log("[useUserProfile] Kudoz balance set to:", data.balance);
        }
      }
    } catch (err) {
      logger.error("[useUserProfile] Failed to fetch Kudoz balance:", err);
    }
  }, [token]);

  useEffect(() => {
    logger.log(
      "[UserProfileProvider] Effect triggered - isAuthenticated:",
      isAuthenticated,
      "token exists:",
      !!token
    );
    if (isAuthenticated && token) {
      logger.log(
        "[UserProfileProvider] Fetching profile, addresses, phones, and syncing Kudoz wallet..."
      );
      setIsLoading(true);
      setError(null);

      // Sync Kudoz wallet FIRST, then fetch balance after sync completes
      const syncAndFetchKudoz = async () => {
        await syncKudozWallet();
        await fetchKudozBalance();
      };

      Promise.all([
        fetchProfile(),
        fetchAddresses(),
        fetchPhones(),
        syncAndFetchKudoz(),
      ])
        .then(() => {
          logger.log("[UserProfileProvider] Fetch completed");
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      logger.log("[UserProfileProvider] Not authenticated, clearing data");
      setProfile(null);
      setAddresses([]);
      setPhones([]);
      setKudozBalance(0);
      setKudozSynced(false);
      setKudozLinked(true);
      localStorage.removeItem("user_addresses_cache");
    }
  }, [
    isAuthenticated,
    token,
    fetchProfile,
    fetchAddresses,
    fetchPhones,
    syncKudozWallet,
    fetchKudozBalance,
  ]);

  const refreshProfile = async () => {
    await fetchProfile();
  };

  const refreshAddresses = async () => {
    await fetchAddresses();
  };

  const refreshPhones = async () => {
    await fetchPhones();
  };

  const refreshKudozBalance = async () => {
    await Promise.all([syncKudozWallet(), fetchKudozBalance()]);
  };

  const addAddress = async (
    address: Omit<SavedAddress, "id">
  ): Promise<SavedAddress | null> => {
    if (!token) return null;

    try {
      const addition: Record<string, string> = {};
      if (address.label) addition.label = address.label;
      if (address.apartment) addition.apartment = address.apartment;
      if (address.doorCode) addition.doorcode = address.doorCode;
      if (address.businessName) addition.company = address.businessName;
      if (address.deliveryInstructions)
        addition.notes = address.deliveryInstructions;

      const payload = {
        data: {
          address: address.streetAddress,
          city: address.city,
          state: address.state || "",
          zip: address.zipCode,
          primary: address.isPrimary ?? false,
          latitude: address.latitude,
          longitude: address.longitude,
          ...(Object.keys(addition).length > 0 ? { addition } : {}),
          entered_address: address.streetAddress,
        },
      };

      logger.log(
        "[useUserProfile] Saving address with payload:",
        JSON.stringify(payload)
      );

      const response = await authService.eatzyApiCall("/account/address/new", {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        logger.log(
          "[useUserProfile] Save address response:",
          JSON.stringify(data)
        );

        await fetchAddresses();

        const savedAddress: SavedAddress = {
          id: data?.data?.address_id || data?.address?.address_id || Date.now(),
          label: address.label,
          streetAddress: address.streetAddress,
          apartment: address.apartment,
          city: address.city,
          state: address.state,
          zipCode: address.zipCode,
          country: address.country || "US",
          latitude: address.latitude,
          longitude: address.longitude,
          isPrimary: address.isPrimary ?? false,
          deliveryInstructions: address.deliveryInstructions,
          placeId: address.placeId,
        };
        return savedAddress;
      } else {
        const errorText = await response.text();
        logger.error(
          "[useUserProfile] Save address failed:",
          response.status,
          errorText
        );
      }
      return null;
    } catch (err) {
      logger.error("[useUserProfile] Failed to add address:", err);
      return null;
    }
  };

  const updateAddress = async (
    id: number,
    address: Partial<SavedAddress>
  ): Promise<boolean> => {
    if (!token) return false;

    try {
      const payload: Record<string, unknown> = {};

      if (address.label !== undefined) payload.label = address.label;
      if (address.streetAddress !== undefined)
        payload.street_address = address.streetAddress;
      if (address.apartment !== undefined)
        payload.apartment = address.apartment;
      if (address.city !== undefined) payload.city = address.city;
      if (address.state !== undefined) payload.state = address.state;
      if (address.zipCode !== undefined) payload.zip_code = address.zipCode;
      if (address.country !== undefined) payload.country = address.country;
      if (address.latitude !== undefined) payload.latitude = address.latitude;
      if (address.longitude !== undefined)
        payload.longitude = address.longitude;
      if (address.deliveryInstructions !== undefined) {
        payload.delivery_instructions = address.deliveryInstructions;
      }

      const response = await authService.eatzyApiCall(
        "/account/address/update",
        {
          method: "PUT",
          body: JSON.stringify({
            address_id: id,
            ...payload,
          }),
        }
      );

      if (response.ok) {
        await fetchAddresses();
        return true;
      }
      return false;
    } catch (err) {
      logger.error("[useUserProfile] Failed to update address:", err);
      return false;
    }
  };

  const deleteAddress = async (id: number): Promise<boolean> => {
    logger.log(
      "[useUserProfile] deleteAddress called with id:",
      id,
      "token exists:",
      !!token
    );

    if (!token) {
      logger.error("[useUserProfile] No token available for deleteAddress");
      return false;
    }

    try {
      logger.log(
        "[useUserProfile] Making DELETE request to /api/user/addresses/" + id
      );
      // Use the dedicated user address delete endpoint, not the generic proxy
      const response = await fetch(`/api/user/addresses/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      logger.log("[useUserProfile] Delete response status:", response.status);

      if (response.ok) {
        logger.log("[useUserProfile] Successfully deleted address:", id);
        await fetchAddresses();
        return true;
      }
      const errorText = await response.text();
      logger.warn(
        "[useUserProfile] Failed to delete address:",
        id,
        "status:",
        response.status,
        "error:",
        errorText
      );
      return false;
    } catch (err) {
      logger.error("[useUserProfile] Failed to delete address:", err);
      return false;
    }
  };

  const setPrimaryAddress = async (id: number): Promise<boolean> => {
    if (!token) return false;

    try {
      const response = await authService.eatzyApiCall(
        "/account/address/primary",
        {
          method: "PUT",
          body: JSON.stringify({ address_id: id }),
        }
      );

      if (response.ok) {
        await fetchAddresses();
        return true;
      }
      return false;
    } catch (err) {
      logger.error("[useUserProfile] Failed to set primary address:", err);
      return false;
    }
  };

  const cleanupAddresses = async (): Promise<{
    deletedCount: number;
    keptCount: number;
  }> => {
    logger.log("[useUserProfile] Starting address cleanup...");

    // Get fresh addresses from API
    try {
      const response = await authService.eatzyApiCall("/account/address/all");
      if (response.status !== 200) {
        logger.error("[useUserProfile] Failed to fetch addresses for cleanup");
        return { deletedCount: 0, keptCount: addresses.length };
      }

      const data = await response.json();
      const addressList = Array.isArray(data) ? data : data.addresses || [];
      logger.log(
        "[useUserProfile] Found",
        addressList.length,
        "addresses to analyze"
      );

      if (addressList.length <= 1) {
        return { deletedCount: 0, keptCount: addressList.length };
      }

      // Create a map to track unique addresses
      // Key: normalized address string (street + city + state + zip)
      const uniqueAddresses = new Map<
        string,
        { id: string | number; isPrimary: boolean; isActive: boolean }
      >();
      const duplicateIds: number[] = [];

      for (const addr of addressList) {
        const street = (addr.address || addr.street_address || "")
          .toLowerCase()
          .trim();
        const city = (addr.city || "").toLowerCase().trim();
        const state = (addr.state || "").toLowerCase().trim();
        const zip = (addr.zip || addr.zip_code || "").toLowerCase().trim();

        // Skip addresses with missing key fields - can't reliably dedupe them
        if (!street && !city && !zip) {
          logger.log(
            "[useUserProfile] Skipping address with missing key fields:",
            addr.id || addr.address_id
          );
          continue;
        }

        const key = `${street}|${city}|${state}|${zip}`;
        const addrId = addr.id || addr.address_id;
        const isPrimary = addr.primary ?? addr.is_primary ?? false;
        const isActive = addr.active ?? addr.is_active ?? true; // Default to active if not specified

        if (uniqueAddresses.has(key)) {
          // This is a duplicate
          const existing = uniqueAddresses.get(key);
          if (!existing) {
            uniqueAddresses.set(key, { id: addrId, isPrimary, isActive });
            continue;
          }
          // Priority: primary > active > first occurrence
          const shouldReplaceExisting =
            (isPrimary && !existing.isPrimary) ||
            (isActive && !existing.isActive && !existing.isPrimary);

          if (shouldReplaceExisting) {
            // This one has higher priority, swap it
            const existing = uniqueAddresses.get(key);
            if (!existing) {
              uniqueAddresses.set(key, { id: addrId, isPrimary, isActive });
              continue;
            }
            uniqueAddresses.set(key, { id: addrId, isPrimary, isActive });
          } else {
            // Keep existing, mark this as duplicate
            duplicateIds.push(addrId);
          }
        } else {
          uniqueAddresses.set(key, { id: addrId, isPrimary, isActive });
        }
      }

      logger.log(
        "[useUserProfile] Found",
        duplicateIds.length,
        "duplicate addresses to delete:",
        duplicateIds
      );
      logger.log("[useUserProfile] Token exists for cleanup:", !!token);

      // Delete duplicates
      let deletedCount = 0;
      for (const id of duplicateIds) {
        try {
          logger.log("[useUserProfile] Cleanup: deleting address id:", id);
          // Use the dedicated user address delete endpoint, not the generic proxy
          const deleteResponse = await fetch(`/api/user/addresses/${id}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          logger.log(
            "[useUserProfile] Cleanup delete response for",
            id,
            "status:",
            deleteResponse.status
          );
          if (deleteResponse.ok) {
            deletedCount++;
            logger.log("[useUserProfile] Deleted duplicate address:", id);
          } else {
            const errorText = await deleteResponse.text();
            logger.warn(
              "[useUserProfile] Failed to delete address:",
              id,
              "status:",
              deleteResponse.status,
              "error:",
              errorText
            );
          }
        } catch (err) {
          logger.error("[useUserProfile] Error deleting address:", id, err);
        }
      }

      // Refresh addresses after cleanup
      await refreshAddresses();

      const keptCount = addressList.length - deletedCount;
      logger.log(
        "[useUserProfile] Cleanup complete. Deleted:",
        deletedCount,
        "Kept:",
        keptCount
      );

      return { deletedCount, keptCount };
    } catch (err) {
      logger.error("[useUserProfile] Failed to cleanup addresses:", err);
      return { deletedCount: 0, keptCount: addresses.length };
    }
  };

  return (
    <UserProfileContext.Provider
      value={{
        profile,
        addresses,
        phones,
        primaryAddress,
        kudozBalance,
        kudozSynced,
        kudozLinked,
        unreadAlertCount,
        isLoading,
        error,
        refreshProfile,
        refreshAddresses,
        refreshPhones,
        refreshKudozBalance,
        updateKudozBalance: (newBalance: number) => {
          logger.log("[useUserProfile] Updating Kudoz balance to:", newBalance);
          setKudozBalance(newBalance);
        },
        addAddress,
        updateAddress,
        deleteAddress,
        setPrimaryAddress,
        cleanupAddresses,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile() {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error("useUserProfile must be used within a UserProfileProvider");
  }
  return context;
}

export function useUserProfileOptional() {
  return useContext(UserProfileContext);
}
